import type { Location, ParametersLocation } from "@/lib/api";

/**
 * Ordered seed list of well-known KNMI main stations, highest priority first.
 *
 * SEED ONLY: this is a placeholder to be replaced by Albert's final, curated
 * priority list. Stations are matched by NORMALIZED NAME (case-, diacritics-
 * and whitespace-insensitive) so the list stays human-editable — just type the
 * station name as a human would and ordering follows array order below.
 */
export const STANDARD_STATION_PRIORITY: string[] = [
  "De Bilt",
  "Schiphol",
  "Rotterdam",
  "Eelde",
  "De Kooy",
  "Vlissingen",
  "Maastricht",
  "Twente",
  "Leeuwarden",
];

/**
 * Normalize a station name for human-editable matching: lowercase, strip
 * diacritics, and collapse whitespace. Used to match against
 * {@link STANDARD_STATION_PRIORITY}.
 */
function normalizeStationName(name: string | undefined): string {
  return (name ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

const STANDARD_STATION_PRIORITY_RANK = new Map<string, number>(
  STANDARD_STATION_PRIORITY.map((name, index) => [normalizeStationName(name), index]),
);

/**
 * Return a NEW array of stations sorted by priority (highest first). Ranking:
 *  1. Position in the dashboard-location order (array order of
 *     `dashboardLocations`, mapped via `parameter_location_id` ->
 *     `ParametersLocation.id`).
 *  2. Otherwise position in {@link STANDARD_STATION_PRIORITY} (by normalized name).
 *  3. Otherwise a stable fallback (alphabetical by name).
 *
 * Sort is stable and the input array is never mutated.
 */
export function orderStationsByPriority(
  locations: ParametersLocation[],
  dashboardLocations: Location[] | undefined,
): ParametersLocation[] {
  const dashboardRankById = new Map<string, number>();
  let dashboardIndex = 0;
  for (const dashboardLocation of dashboardLocations ?? []) {
    const id = dashboardLocation.parameter_location_id?.trim();
    if (id && !dashboardRankById.has(id)) {
      dashboardRankById.set(id, dashboardIndex);
      dashboardIndex += 1;
    }
  }

  // Tiers keep dashboard order strictly above the standard list, which stays
  // strictly above the alphabetical fallback, regardless of index magnitudes.
  const DASHBOARD_TIER = 0;
  const STANDARD_TIER = 1;
  const FALLBACK_TIER = 2;

  const ranked = locations.map((location, originalIndex) => {
    const dashboardRank = dashboardRankById.get(location.id);
    if (dashboardRank !== undefined) {
      return { location, originalIndex, tier: DASHBOARD_TIER, rank: dashboardRank };
    }
    const standardRank = STANDARD_STATION_PRIORITY_RANK.get(
      normalizeStationName(location.name),
    );
    if (standardRank !== undefined) {
      return { location, originalIndex, tier: STANDARD_TIER, rank: standardRank };
    }
    return { location, originalIndex, tier: FALLBACK_TIER, rank: 0 };
  });

  ranked.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.tier === FALLBACK_TIER) {
      const byName = (a.location.name ?? "").localeCompare(b.location.name ?? "");
      if (byName !== 0) return byName;
      return a.originalIndex - b.originalIndex;
    }
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.originalIndex - b.originalIndex;
  });

  return ranked.map((entry) => entry.location);
}

export function collectParameterLocationIds(
  dashboardLocations: Location[] | undefined,
): Set<string> {
  const ids = new Set<string>();
  for (const dashboardLocation of dashboardLocations ?? []) {
    const id = dashboardLocation.parameter_location_id?.trim();
    if (id) ids.add(id);
  }
  return ids;
}

export function filterParametersLocationsByDashboardLinks(
  allLocations: ParametersLocation[],
  dashboardLocations: Location[] | undefined,
): ParametersLocation[] {
  const linkedIds = collectParameterLocationIds(dashboardLocations);
  if (linkedIds.size === 0) return [];
  return allLocations.filter((location) => linkedIds.has(location.id));
}

export function countDashboardLocationsWithParameterLink(
  dashboardLocations: Location[] | undefined,
): number {
  return collectParameterLocationIds(dashboardLocations).size;
}
